import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Center,
  chakra,
  FlexProps,
  forwardRef,
  HStack,
  VStack,
} from '@chakra-ui/react';
import { IconName } from '@shinetools/sunshine-icons';

import Heading from 'components/_core/Heading';
import IconButton from 'components/_core/IconButton';
import SunshineIcon from 'components/_core/SunshineIcon';
import Text from 'components/_core/Text';

import { useDetailSidebar } from './';
import { useStyles } from './context';

interface DetailSidebarHeaderProps extends FlexProps {
  heading?: ReactNode;
  label?: ReactNode;
  iconName?: IconName;
}

const DetailSidebarHeader = forwardRef<DetailSidebarHeaderProps, 'header'>(
  ({ heading, iconName, label, ...props }, ref) => {
    const styles = useStyles();
    const { backTo, closeTo, onBack, onClose } = useDetailSidebar();

    return (
      <chakra.header __css={styles.header} ref={ref} {...props}>
        {closeTo || backTo || onClose || onBack ? (
          <HStack justifyContent="space-between">
            {backTo || onBack ? (
              <IconButton
                aria-label="close"
                sx={styles.closeButton}
                {...(onBack ? { onClick: onBack } : {})}
                {...(backTo ? { as: Link, to: backTo } : {})}
                icon="arrow-left"
                variant="secondary"
              />
            ) : null}
            {closeTo || onClose ? (
              <IconButton
                aria-label="close"
                marginLeft="auto"
                sx={styles.closeButton}
                {...(onClose ? { onClick: onClose } : {})}
                {...(closeTo ? { as: Link, to: closeTo } : {})}
                icon="nav-close"
                variant="secondary"
              />
            ) : null}
          </HStack>
        ) : null}
        {heading || label ? (
          <VStack spacing="space-0">
            {heading ? (
              <Heading
                fontWeight="weight-400"
                lineHeight="shortest"
                size="2xl"
                variant="number"
              >
                {heading}
              </Heading>
            ) : null}
            {label ? (
              <Text textAlign="center" variant="secondary">
                {label}
              </Text>
            ) : null}
          </VStack>
        ) : null}
        {iconName ? (
          <Center __css={styles.headerIcon}>
            <SunshineIcon color="primary" name={iconName} size="icon-26" />
          </Center>
        ) : null}
      </chakra.header>
    );
  },
);

DetailSidebarHeader.displayName = 'DetailSidebarHeader';

export default DetailSidebarHeader;
